// One value on several lines: the selection bar's Set value…, the grid's fill down (Ctrl+D, the fill
// handle) and a paste of several lines. Every cell is checked as a typed one is (`checkCellValue`,
// the grid's own commit uses it too); a line the person could not edit by hand is never written
// (No WMS, finished, History); a line that has the value already is skipped. The writes are the
// writer's (`saveMany`): one record and one change entry per line, one Undo for all. No React: the
// checks (`scripts/routing-checks.ts`) run it.

import {
  PE_CLEARED,
  ROUTING_LINES,
  appointmentCell,
  checkDate,
  checkStartEnd,
  isMulti,
  joinValues,
  parseDateInput,
  parseEstimate,
  parseMoment,
  parseWhole,
  splitValues,
} from "@app/shared";
import type { CrudField, DateCell, ListValueRecord, Mode, Row } from "@app/shared";
import type { ColumnDef } from "./columns";

const fieldMax = (field: string) =>
  (ROUTING_LINES.fields as readonly CrudField[]).find((f) => f.name === field)?.max ?? 200;

/** A cell's value checked: as stored, with the "Are you sure?" lines a date rule gives; or refused. */
export type CellCheck = { ok: true; value: string; warnings: string[] } | { ok: false; title: string; message: string };

export interface CheckContext {
  today: string;
  listValues: readonly ListValueRecord[];
}

/** Whether a person may type in this cell of this row: an edit column of an open line on the Worklist. */
export const canEditCell = (def: ColumnDef, row: Row, mode: Mode) =>
  mode === "worklist" && def.kind === "edit" && !!def.field && !row.line.noWms && !row.line.finished;

/**
 * What a cell holds for a copy (a fill's source): what it shows, as stored — a prefill's value, the
 * Appointment date's pickup day; PE Order Status emptied on purpose is empty.
 */
export function cellValue(def: ColumnDef, row: Row): string {
  if (!def.field) return "";
  if (def.field === "appointmentDate") return appointmentCell(row.line).value;
  const stored = row.line.fields[def.field] ?? "";
  if (def.field === "peStatus" && stored === PE_CLEARED) return "";
  const pre = def.prefill?.(row);
  return pre?.prefill && !stored ? pre.value : stored;
}

/** A cell's value now, for "already has this value": the pickup's day for the Appointment date. */
const before = (def: ColumnDef, row: Row) =>
  def.field === "appointmentDate" ? appointmentCell(row.line).value : (row.line.fields[def.field!] ?? "");

/**
 * The checks a typed value goes through (the grid's commit, and every cell of a batch): a date that
 * parses and is no wrong year (its "when" rules: `warnings`), a list value that exists (one already on
 * the line may be retired since), an estimate, a whole number, a moment and End after Start, a text's
 * limit. A prefill left as it is stays a prefill (""); PE Order Status emptied is stored `PE_CLEARED`.
 */
export function checkCellValue(def: ColumnDef, row: Row, raw: string, ctx: CheckContext): CellCheck {
  const line = row.line;
  const field = def.field!;
  const was = line.fields[field] ?? "";
  const refuse = (message: string): CellCheck => ({ ok: false, title: `Can't save ${def.header}`, message });
  let value = raw.trim();
  let warnings: string[] = [];
  if (def.prefill?.(row).prefill && !was && value === def.value(row)) value = "";
  if (def.editor === "date") {
    const parsed = parseDateInput(value, field as DateCell, ctx.today);
    if (!parsed.ok) return refuse(parsed.message);
    value = parsed.value;
    const problems = checkDate(field as DateCell, value, {
      today: ctx.today,
      ptDate: line.ptDate,
      start: line.start,
      cancel: line.cancel,
      readyDate: line.fields.readyDate,
      schedShip: line.fields.schedShip,
      onPickup: !!line.onPickup,
    });
    // Blocked only when it is no valid date; a rule about when is an "Are you sure?" (`DATE_RULES`).
    const error = problems.find((p) => p.level === "error");
    if (error) return refuse(error.message);
    warnings = problems.map((p) => p.message);
  } else if (def.editor === "list" && value && value !== was) {
    const had = splitValues(was);
    const off = (isMulti(field) ? splitValues(value) : [value]).find(
      (one) =>
        !had.includes(one) &&
        !ctx.listValues.some((v) => v.list === def.list && v.retired !== "Yes" && v.value === one),
    );
    if (off) return refuse(`“${off}” isn't on the ${def.header} list — pick one, or choose “Add as a new value”.`);
  } else if (def.editor === "estimate" || def.editor === "whole") {
    const parsed = def.editor === "estimate" ? parseEstimate(value) : parseWhole(value, def.header);
    if (!parsed.ok) return refuse(parsed.message);
    value = parsed.value === null ? "" : String(parsed.value);
  } else if (def.editor === "moment") {
    // "10/30 14:05", "10/30/26 14:05" (the picker, Now, the cell), "2026-10-30 14:05" (`parseMoment`).
    const parsed = parseMoment(value, ctx.today);
    if (!parsed.ok) return refuse(parsed.message);
    value = parsed.value;
    const other = field === "peStartAt" ? line.fields.peEndAt : line.fields.peStartAt;
    const problem = field === "peStartAt" ? checkStartEnd(value, other) : checkStartEnd(other, value);
    if (problem) return refuse(problem);
  } else if (value.length > fieldMax(field)) {
    return refuse(`${def.header} is at most ${fieldMax(field)} characters.`);
  }
  // PE Order Status emptied: stored empty on purpose, so no prefill comes back.
  if (field === "peStatus" && !value && was) value = PE_CLEARED;
  return { ok: true, value, warnings };
}

/** Set value's way with a field of several values (Work Needed, Assigned to): Replace, or Add to each line's. */
export type MultiMode = "replace" | "add";

/** One cell of a batch: its row and the value typed, copied or pasted for it. */
export interface FillTarget {
  row: Row;
  raw: string;
}

export interface FillPlan {
  /** The lines to save, with the value each gets (as stored). */
  writes: { row: Row; value: string }[];
  /** Lines that have the value already: skipped. */
  same: Row[];
  /** Lines nobody may type in (No WMS, finished): skipped. */
  readOnly: Row[];
  /** The first cell whose value a check refused; the cells after it are not planned. */
  refused: { row: Row; raw: string; title: string; message: string } | null;
  /** The date rules' "Are you sure?" lines, each with its line. */
  warnings: { row: Row; messages: string[] }[];
}

/**
 * Every cell of a batch, checked as typed: what is written, skipped as the same, skipped as read-only,
 * and the first value refused — a Set value or a fill down writes nothing then, a paste what came before it.
 */
export function planFill(
  def: ColumnDef,
  targets: readonly FillTarget[],
  ctx: CheckContext & { mode: Mode; multi?: MultiMode },
): FillPlan {
  const plan: FillPlan = { writes: [], same: [], readOnly: [], refused: null, warnings: [] };
  for (const { row, raw } of targets) {
    if (!canEditCell(def, row, ctx.mode)) {
      plan.readOnly.push(row);
      continue;
    }
    const had = before(def, row);
    // Add to what each line has: its values and the new ones, each once.
    const typed =
      ctx.multi === "add" && isMulti(def.field) ? joinValues([...splitValues(had), ...splitValues(raw)]) : raw;
    const checked = checkCellValue(def, row, typed, ctx);
    if (!checked.ok) {
      plan.refused = { row, raw, title: checked.title, message: checked.message };
      break;
    }
    // A prefill left as it is (""), or the value the line has: nothing to save.
    const pre = def.prefill?.(row);
    if (
      checked.value === had ||
      (!checked.value && pre?.prefill && !had) ||
      (pre?.prefill && !had && checked.value === pre.value)
    ) {
      plan.same.push(row);
      continue;
    }
    plan.writes.push({ row, value: checked.value });
    if (checked.warnings.length) plan.warnings.push({ row, messages: checked.warnings });
  }
  return plan;
}

/**
 * Where a fill down takes its value and which rows it fills, by row index: a range (`from` where it
 * started, `to` where it was extended to) fills from its top cell (Ctrl+D) or from where it started
 * (`origin`: the fill handle, dragged down or up); no range, the cell above fills the cursor's (Excel's
 * single-cell Ctrl+D). Null: nothing to fill.
 */
export function fillRows(
  range: { from: number; to: number } | null,
  cursor: number,
  source: "top" | "origin" = "top",
): { source: number; targets: number[] } | null {
  if (!range || range.from === range.to) return cursor > 0 ? { source: cursor - 1, targets: [cursor] } : null;
  const lo = Math.min(range.from, range.to);
  const hi = Math.max(range.from, range.to);
  const from = source === "origin" ? range.from : lo;
  const targets: number[] = [];
  for (let i = lo; i <= hi; i++) if (i !== from) targets.push(i);
  return { source: from, targets };
}

/**
 * A paste's values, one per line as Excel copies a column: the trailing line break dropped, a
 * row of several cells (tabs) its first cell. One value: the cell's own paste (its editor opens).
 */
export function pastedValues(text: string): string[] {
  const lines = text.replace(/\r?\n$/, "").split(/\r?\n/);
  return lines.map((l) => (l.split("\t")[0] ?? "").trim());
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/** "Sets READY DATE on 14 lines · 3 already have this value (skipped) · 2 are read-only (No WMS, skipped)". */
export function fillPreview(def: ColumnDef, plan: FillPlan): string {
  const noWms = plan.readOnly.filter((r) => r.line.noWms).length;
  const finished = plan.readOnly.length - noWms;
  const why = [noWms ? "No WMS" : "", finished ? "finished" : ""].filter(Boolean).join(", ");
  return [
    `Sets ${def.header} on ${plural(plan.writes.length, "line")}`,
    plan.same.length
      ? `${plan.same.length.toLocaleString("en-US")} already ${plan.same.length === 1 ? "has" : "have"} this value (skipped)`
      : "",
    plan.readOnly.length
      ? `${plan.readOnly.length.toLocaleString("en-US")} ${plan.readOnly.length === 1 ? "is" : "are"} read-only (${why}, skipped)`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The System log's note on each line a batch saved: a reader sees it was one of several. */
export function batchNote(kind: "set" | "fill" | "paste", lines: number): string {
  const others = lines - 1;
  if (others < 1) return kind === "fill" ? "Filled down" : kind === "paste" ? "Pasted" : "";
  const what = kind === "set" ? "Set" : kind === "fill" ? "Filled down" : "Pasted";
  return `${what} with ${plural(others, "other line")}`;
}
